import { useCallback, useEffect, useState, useRef } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import GaugePlot from "./GaugePlot";
import {
  getEdgeQuantifier,
  getNodeSetNodes,
  getNetworkNodeSets,
} from "../api/quontxApi";
import {
  resetState,
  saveToState,
  loadFromState,
  getPaginatedData,
} from "../utils";

const EdgeAnalysisSemanticFilters = ({ propagnateData }) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [nodeOptionsA, setNodeOptionsA] = useState([]);
  const [nodeOptionsB, setNodeOptionsB] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [comparisonMessage, setComparisonMessage] = useState(null);

  const [nodeA, setNodeA] = useState(null);
  const [nodeB, setNodeB] = useState(null);
  const [nodeSetA, setNodeSetA] = useState(null);
  const [nodeSetB, setNodeSetB] = useState(null);

  const resetFilters = () => {
    resetState("edgeAnalysisSemnaticState", [
      {
        stateFunc: setNodeA,
        nullVal: null,
      },
      {
        stateFunc: setNodeB,
        nullVal: null,
      },
      {
        stateFunc: setNodeSetA,
        nullVal: null,
      },
      {
        stateFunc: setNodeSetB,
        nullVal: null,
      },
    ]);
    setComparisonMessage(null);
    setHasNetworkError(false);
  };

  useEffect(() => {
    loadFromState("edgeAnalysisSemnaticState", [
      {
        stateFunc: setNodeA,
        storageObjectKey: "nodeA",
        defaultVal: null,
      },
      {
        stateFunc: setNodeB,
        storageObjectKey: "nodeB",
        defaultVal: null,
      },
      {
        stateFunc: setNodeSetA,
        storageObjectKey: "nodeSetA",
        defaultVal: null,
      },
      {
        stateFunc: setNodeSetB,
        storageObjectKey: "nodeSetB",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  const loadNodeOptionsA = useCallback(async () => {
    const nodes = await getPaginatedData(getNodeSetNodes, {
      itemsPerPage: 100,
      nodeSetId: nodeSetA.uuid,
    });
    setNodeOptionsA(nodes.data);
  }, [nodeSetA]);

  const loadNodeOptionsB = useCallback(async () => {
    const nodes = await getPaginatedData(getNodeSetNodes, {
      itemsPerPage: 100,
      nodeSetId: nodeSetB.uuid,
    });
    setNodeOptionsB(nodes.data);
  }, [nodeSetB]);

  const loadNodeSetOptions = async (inputValue) => {
    if (!inputValue) return [];
    const nodeSets = await getNetworkNodeSets({
      itemsPerPage: 100,
      name: inputValue,
    });

    return nodeSets.data.data.map((node) => {
      return {
        label: node.description,
        value: node.qids[0],
        uuid: node.uuid,
      };
    });
  };

  const loadComparison = useCallback(async (nodes) => {
    try {
      setHasNetworkError(false);
      const causilityScore = await getEdgeQuantifier({
        nodes,
        edgeType: "semantic relatedness",
      });

      propagnateFunctionRef.current(causilityScore.data);
      setData(causilityScore.data);
    } catch (e) {
      console.error(`Failed to load comparison: ${e}`);
      setHasNetworkError(true);
    }
  }, []);

  useEffect(() => {
    if (!nodeSetA) return;
    (async () => {
      await loadNodeOptionsA();
    })();
  }, [nodeSetA, loadNodeOptionsA]);

  useEffect(() => {
    if (!nodeSetB) return;
    (async () => {
      await loadNodeOptionsB();
    })();
  }, [nodeSetB, loadNodeOptionsB]);

  useEffect(() => {
    if (
      nodeA !== null ||
      nodeB !== null ||
      nodeSetA !== null ||
      nodeSetB !== null
    ) {
      saveToState("edgeAnalysisSemnaticState", {
        nodeA: nodeA,
        nodeB: nodeB,
        nodeSetA: nodeSetA,
        nodeSetB: nodeSetB,
      });
    }
  }, [nodeA, nodeB, nodeSetA, nodeSetB]);

  useEffect(() => {
    if (!nodeSetA) {
      setNodeA(null);
      setComparisonMessage("");
      return;
    }
    if (!nodeSetB) {
      setNodeB(null);
      setComparisonMessage("");
      return;
    }
    if ((!nodeA && !nodeSetA) || (!nodeB && !nodeSetB)) {
      setComparisonMessage("");
      return;
    }

    (async () => {
      // Determine which nodes or nodesets to compare
      let pair = [];
      if (!nodeA && !nodeB && nodeSetA && nodeSetB) {
        setComparisonMessage(
          `Comparing node set ${nodeSetA.label} with node set ${nodeSetB.label}`
        );
        pair = [nodeSetA.value, nodeSetB.value];
      } else if (nodeA && nodeSetA && !nodeB && nodeSetB) {
        setComparisonMessage(
          `Comparing node ${nodeA.label} with node set ${nodeSetB.label}`
        );
        pair = [nodeA.value, nodeSetB.value];
      } else if (!nodeA && nodeSetA && nodeB && nodeSetB) {
        setComparisonMessage(
          `Comparing node set ${nodeSetA.label} with node ${nodeB.label}`
        );
        pair = [nodeSetA.value, nodeB.value];
      } else if (nodeA && nodeB && nodeSetA && nodeSetB) {
        setComparisonMessage(
          `Comparing node ${nodeA.label} with node ${nodeB.label}`
        );
        pair = [nodeA.value, nodeB.value];
      }

      // Load comparison
      setLoadingData(true);
      await loadComparison(pair);
      setLoadingData(false);
    })();
  }, [loadComparison, nodeA, nodeB, nodeSetA, nodeSetB]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      <div className="flex flex-col gap-4 items-center justify-center mb-8">
        <div className="flex flex-col gap-4 items-center justify-center mb-8 w-full">
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node set A</p>
              <AsyncSelect
                cacheOptions
                defaultOptions={[]}
                loadOptions={loadNodeSetOptions}
                onChange={(selected) => setNodeSetA(selected)}
                value={nodeSetA}
                placeholder="Search node set..."
                isClearable
              />
            </div>
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node set B</p>
              <AsyncSelect
                cacheOptions
                defaultOptions={[]}
                loadOptions={loadNodeSetOptions}
                onChange={(selected) => setNodeSetB(selected)}
                value={nodeSetB}
                placeholder="Search node set..."
                isClearable
              />
            </div>
          </div>
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node A</p>
              <Select
                isSearchable={true}
                isDisabled={!nodeSetA}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isDisabled ? "red" : "grey",
                  }),
                }}
                value={nodeA}
                onChange={(selected) => setNodeA(selected)}
                options={nodeOptionsA.map((node) => {
                  return { label: node.name, value: node.uuid };
                })}
                isClearable
              />
            </div>
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node B</p>
              <Select
                isSearchable={true}
                isDisabled={!nodeSetB}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isDisabled ? "red" : "grey",
                  }),
                }}
                value={nodeB}
                onChange={(selected) => setNodeB(selected)}
                options={nodeOptionsB.map((node) => {
                  return { label: node.name, value: node.uuid };
                })}
                isClearable
              />
            </div>
          </div>
          {comparisonMessage && <p>{comparisonMessage}</p>}
          <div>
            <button
              onClick={() => resetFilters()}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>
        </div>
        <div className="flex items-center content-center flex-wrap">
          {loadingData && <p>Loading data...</p>}
          {!loadingData &&
            (data !== null || data !== undefined) &&
            (nodeA || nodeSetA) &&
            (nodeB || nodeSetB) &&
            !hasNetworkError && <GaugePlot title={"Similarity"} value={data} />}
        </div>
        {hasNetworkError && <p>Failed to load comparison data</p>}
      </div>
    </div>
  );
};

export default EdgeAnalysisSemanticFilters;
